import { parseContextSession, type ContextSessionResponse } from "@kontextwerk/control-web-client"
import type { createContextRootClient } from "@kontextwerk/control-web-frame/shared/logic/context-runtime"
import { createOrderTypesContextApi } from "../../../../../../../api/kehrblatt/order-types-context"
import { contextOrderTypesPage } from "../config"
import type { OrderTypesBinding } from "./binding"

export const createContextOrderTypes = (
  client: ReturnType<typeof createContextRootClient>,
  value: ContextSessionResponse,
  organizationId: string,
) => {
  const session = parseContextSession(value)
  const page = contextOrderTypesPage(session, organizationId)
  const capture = page ? client.root.captureMutation() : null
  const local = new AbortController()
  const invalidate = () => local.abort()
  const current = () => !local.signal.aborted && !!capture?.requestOptions.isCurrent()
  capture?.requestOptions.signal.addEventListener("abort", invalidate)

  if (capture?.requestOptions.signal.aborted) invalidate()

  const api = capture
    ? createOrderTypesContextApi(client.operations, session, {
        ...capture.requestOptions,
        signal: local.signal,
        isCurrent: current,
      })
    : null
  const execute = async <T>(write: boolean, action: () => Promise<T>): Promise<T> => {
    if (!current() || (write && !page?.canManage))
      throw new DOMException("Unconfirmed order-type access", "InvalidStateError")

    try {
      return await action()
    } catch (error) {
      if ([401, 403, 409].includes((error as { statusCode?: number })?.statusCode ?? 0))
        client.root.invalidate("revoked")

      throw error
    }
  }
  const binding: OrderTypesBinding | null =
    api && page
      ? {
          organizationId,
          canManage: page.canManage,
          signal: local.signal,
          isCurrent: current,
          list: (signal) => execute(false, () => api.list(signal)),
          create: (payload, signal) => execute(true, () => api.create(payload, signal)),
          update: (id, payload, signal) => execute(true, () => api.update(id, payload, signal)),
          remove: (id, signal) => execute(true, () => api.remove(id, signal)),
          restore: (signal) => execute(true, () => api.restore(signal)),
        }
      : null

  return {
    binding,
    dispose() {
      capture?.requestOptions.signal.removeEventListener("abort", invalidate)
      invalidate()
    },
  }
}
